import classNames from 'classnames'
import {memo, useCallback} from 'react'
import {shallowEqual} from 'react-redux'

import {restApi} from '../../../../services/rest'
import type {ChangeId, VcsRootId} from '../../../../types'
import {stringifyId} from '../../../../types'
import {getModificationsOfChangeArg} from '../../../packages/Changes/Changes.rest'
import {
  getChange,
  getUniqueVcsRootIds,
  getVcsRootInstance,
} from '../../../packages/Changes/Changes.selectors'

import ChangeVcsRoot from './ChangeVcsRoot'

import styles from './ChangeVcsRoots.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  className?: string
  changeId: ChangeId
}

function ChangeVcsRoots({className, changeId}: Props) {
  const personal = useAppSelector(state => getChange(state, changeId)?.personal)
  const vcsRootInstance = useAppSelector(state => getChange(state, changeId)?.vcsRootInstance)
  const currentVcsRootId = useAppSelector(
    state => getVcsRootInstance(state, vcsRootInstance)?.['vcs-root'],
  )

  const locator = `id:${stringifyId(changeId)},personal:${personal || false}`

  const {isReady} = restApi.endpoints.getModificationsOfChange.useQuery(
    getModificationsOfChangeArg(locator),
    {
      selectFromResult: ({data}) => ({isReady: data != null}),
    },
  )
  const vcsRootIds = useAppSelector(
    state => getUniqueVcsRootIds(state, changeId).filter(id => id !== currentVcsRootId),
    shallowEqual,
  )

  const renderRoot = useCallback(
    (id: VcsRootId | null | undefined, index: number) =>
      id != null ? (
        <span key={id}>
          <ChangeVcsRoot vcsRootId={id} current={currentVcsRootId === id} />
          {index !== vcsRootIds.length - 1 ? ', ' : null}
        </span>
      ) : null,
    [currentVcsRootId, vcsRootIds.length],
  )

  return (
    <div className={classNames(className, styles.wrapper)}>
      {isReady ? (
        <>
          {renderRoot(currentVcsRootId, -1)}
          {vcsRootIds.map(renderRoot)}
        </>
      ) : (
        'Loading...'
      )}
    </div>
  )
}

export default memo(ChangeVcsRoots)
